// Turning a whole traced course into one diagram.
//
// The sibling of `holePlan`, and deliberately not a generalisation of it: the two want
// opposite things. A hole plan rotates the ground so the shot reads bottom-to-top and
// crops to the corridor in front of you. A course plan must not rotate anything — north
// is up, the way every course map ever printed has it — and must show everything at
// once, cart paths and clubhouse included, because "where is the 12th from here" is the
// only question it answers.
//
// Pure and DOM-free, like its sibling, so the geometry can be tested without a browser.
import type { CourseMap, LatLng } from "./types.js";
import { centroid, toLocalYards } from "./geo.js";
import {
  LANDMARK_NAMES,
  type PlanLandmark,
  type PlanPath,
  type PlanPoint,
  type PlanShape,
} from "./holePlan.js";

/**
 * Padding inside the viewBox.
 *
 * Generous, because it is not only keeping outlines off the edge: landmark labels are
 * centred on their marker and a clubhouse at the corner of the property would otherwise
 * have half its name clipped by the frame.
 */
const PAD = 26;

/**
 * Longest side of the drawing, in SVG units.
 *
 * Twice the hole plan's, because this frame holds a hundred times the ground. Stroke
 * widths and text are in the same units, so a bigger number is what keeps an outline
 * from being four yards thick at course scale.
 */
const LONG_SIDE = 400;

/** A real cart path is about three yards across, which at this scale is a hairline. */
const MIN_PATH_UNITS = 1;

/** Marks a green that was never outlined, so every traced hole is visible. See below. */
const NOMINAL_GREEN_RADIUS_YARDS = 9;

/** Where one hole is on the course, and what to draw there. */
export interface CourseHoleMark {
  hole: number;
  /** The green center — where the number goes. */
  at: PlanPoint;
  /**
   * Radius for a nominal green blob, or null when the green was properly outlined and
   * that outline is being drawn instead. A hole marked only by a center point would
   * otherwise be a floating number with no ground under it.
   */
  radius: number | null;
}

export interface CoursePlan {
  /** SVG user units; the viewBox is always 0 0 width height. */
  width: number;
  height: number;
  /** Every hole's outlines, flattened. Which hole a shape came from stops mattering
   * here — the picture is the property, not eighteen pictures side by side. */
  shapes: PlanShape[];
  paths: PlanPath[];
  landmarks: PlanLandmark[];
  holes: CourseHoleMark[];
  yardsPerUnit: number;
  /** North is up and nothing is rotated, so this is a straight scale and shift. */
  project(at: LatLng): PlanPoint;
}

/**
 * Project a whole course into one north-up frame.
 *
 * Returns null when there is nothing traced to draw. A half-finished map is fine and
 * shows the holes that exist — this is the screen where a gap is *information*, since it
 * is the only place anyone can see how far the tracing has got.
 */
export function coursePlan(map: CourseMap): CoursePlan | null {
  const all: LatLng[] = [];
  for (const hole of map.holes) {
    for (const p of [hole.greenFront, hole.greenCenter, hole.greenBack]) if (p) all.push(p);
    for (const s of hole.shapes ?? []) all.push(...s.points);
  }
  // Unlike the hole plan, hazards, paths and landmarks *are* framed. There they'd zoom
  // the picture out to the whole property; here the whole property is the subject.
  for (const s of map.shapes ?? []) all.push(...s.points);
  for (const path of map.paths ?? []) all.push(...path.points);
  for (const l of map.landmarks ?? []) all.push(l.at);

  if (all.length === 0) return null;

  const origin = centroid(all);
  const local = all.map((p) => toLocalYards(origin, p));

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of local) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }

  // A course traced down to one green is a legal input; a zero span is not a legal
  // divisor. One yard keeps the scale finite and the single hole centred.
  const spanX = Math.max(maxX - minX, 1);
  const spanY = Math.max(maxY - minY, 1);
  // One scale for both axes. A course stretched to fill the frame would misreport every
  // gap between holes, which is the one thing this drawing is read for.
  const scale = LONG_SIDE / Math.max(spanX, spanY);

  const width = spanX * scale + PAD * 2;
  const height = spanY * scale + PAD * 2;

  // Local yards count north as positive and SVG counts down, hence the flip — which is
  // the whole of the "north is up" claim.
  const place = (p: LatLng): PlanPoint => {
    const q = toLocalYards(origin, p);
    return {
      x: PAD + (q.x - minX) * scale,
      y: height - (PAD + (q.y - minY) * scale),
    };
  };

  const shapes: PlanShape[] = [];
  const holes: CourseHoleMark[] = [];
  // Hazards first, so a bunker never paints over the green it sits beside. On the whole
  // course there is no corridor to test against and no emphasis to decide — every
  // hazard on the property is equally part of the picture.
  for (const s of map.shapes ?? []) {
    if (s.points.length < 3) continue;
    shapes.push({ kind: s.kind, points: s.points.map(place), closed: true });
  }
  for (const hole of [...map.holes].sort((a, b) => a.hole - b.hole)) {
    let outlined = false;
    for (const s of hole.shapes ?? []) {
      if (s.kind === "green") outlined = true;
      shapes.push({ kind: s.kind, points: s.points.map(place), closed: s.kind !== "path" });
    }
    const center = hole.greenCenter ?? hole.greenFront ?? hole.greenBack;
    if (center) {
      holes.push({
        hole: hole.hole,
        at: place(center),
        radius: outlined ? null : NOMINAL_GREEN_RADIUS_YARDS * scale,
      });
    }
  }

  const paths: PlanPath[] = (map.paths ?? [])
    .filter((p) => p.points.length >= 2)
    .map((p) => ({
      points: p.points.map(place),
      // Honest width where the course is small enough for it to be visible, and a
      // legible hairline where it isn't. A path drawn a third of a pixel wide is a
      // path nobody can follow, which defeats the point of having traced it.
      widthUnits: Math.max((p.widthYards ?? 3) * scale, MIN_PATH_UNITS),
    }));

  const landmarks: PlanLandmark[] = (map.landmarks ?? []).map((l) => ({
    kind: l.kind,
    label: l.label?.trim() || LANDMARK_NAMES[l.kind],
    at: place(l.at),
  }));

  return {
    width,
    height,
    shapes,
    paths,
    landmarks,
    holes,
    yardsPerUnit: 1 / scale,
    project: place,
  };
}
